import { Link } from '@tanstack/react-router';
import { pricing } from '@/lib/marketing-data';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Check, Sparkles } from 'lucide-react';

export function PricingSection() {
  return (
    <section className="py-16 sm:py-24 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Simple, Transparent Pricing
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Start free, scale as you grow. No hidden fees, no surprises.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="mt-16 grid gap-8 lg:grid-cols-3">
          {pricing.tiers.map((tier) => (
            <div
              key={tier.name}
              className={cn(
                'relative rounded-2xl border bg-card p-8',
                tier.highlighted &&
                  'border-primary shadow-lg shadow-primary/10 scale-105'
              )}
            >
              {/* Highlighted Badge */}
              {tier.highlighted && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-primary px-4 py-1 text-sm font-semibold text-primary-foreground">
                  <Sparkles className="h-4 w-4" />
                  Most Popular
                </div>
              )}

              {/* Tier Name */}
              <h3 className="text-xl font-semibold">{tier.name}</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                {tier.description}
              </p>

              {/* Price */}
              <div className="mt-6 flex items-baseline">
                <span className="text-4xl font-bold">{tier.price}</span>
                {tier.period && (
                  <span className="ml-1 text-muted-foreground">{tier.period}</span>
                )}
              </div>

              {/* Features */}
              <ul className="mt-8 space-y-4">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <Check className="h-5 w-5 shrink-0 text-primary" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <div className="mt-8">
                <Link to={tier.href} className="block">
                  <Button
                    className="w-full"
                    variant={tier.highlighted ? 'default' : 'outline'}
                    size="lg"
                  >
                    {tier.cta}
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div className="mt-24 mx-auto max-w-3xl">
          <h3 className="text-2xl font-bold text-center mb-8">
            Frequently Asked Questions
          </h3>
          <div className="space-y-6">
            {pricing.faq.map((item, index) => (
              <div key={index} className="rounded-lg border bg-card p-6">
                <h4 className="font-semibold">{item.question}</h4>
                <p className="mt-2 text-sm text-muted-foreground">
                  {item.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function PricingPage() {
  return (
    <div className="pt-24">
      <PricingSection />
    </div>
  );
}
